import { MailCheck } from "lucide-react";
import { formatDate, formatEUR } from "@/lib/format";
import { FulfillmentControl } from "./FulfillmentControl";

const STATUS = {
  paid: ["Pagato", "bg-[#166534]/10 text-[#166534]"],
  pending: ["In attesa", "bg-copper/10 text-copper-deep"],
  expired: ["Scaduto", "bg-navy/10 text-slate2"],
  failed: ["Fallito", "bg-destructive/10 text-destructive"],
  refunded: ["Rimborsato", "bg-navy/10 text-navy"],
};

const COLS = "lg:grid-cols-[150px_100px_minmax(0,1.2fr)_minmax(0,1.3fr)_110px_130px]";
const HEAD = ["Data", "Riferimento", "Cliente", "Articoli", "Totale", "Pagamento"];

const StatusBadge = ({ status }) => {
  const [label, cls] = STATUS[status] ?? [status, "bg-navy/10 text-navy"];
  return <span className={`inline-flex rounded-full px-3 py-1 text-[13px] font-medium ${cls}`}>{label}</span>;
};

const Cell = ({ label, className = "", children }) => (
  <div className={className}>
    <p className="mb-1 text-[12px] uppercase tracking-[0.15em] text-slate2 lg:hidden">{label}</p>
    {children}
  </div>
);

const OrderRow = ({ o }) => {
  const ref = o.session_id.slice(-8);
  return (
    <li data-testid={`order-row-${ref}`} className="px-5 py-6 lg:px-6">
      <div className={`grid grid-cols-2 gap-x-4 gap-y-4 text-[15px] lg:items-start ${COLS}`}>
        <Cell label="Data"><p className="text-slate2">{formatDate(o.created_at)}</p></Cell>
        <Cell label="Riferimento"><p className="font-medium text-navy">{ref.toUpperCase()}</p></Cell>
        <Cell label="Cliente" className="col-span-2 lg:col-span-1">
          <p className="text-navy">{o.customer_name || "—"}</p>
          <p className="break-all text-[13px] text-slate2">{o.customer_email || ""}</p>
          {o.shipping_address && <p className="mt-1 text-[13px] text-slate2">{o.shipping_address}</p>}
          {o.customer_phone && <p className="text-[13px] text-slate2">{o.customer_phone}</p>}
        </Cell>
        <Cell label="Articoli" className="col-span-2 lg:col-span-1">
          <p className="text-navy">{(o.items ?? []).map((i) => `${i.name} ×${i.quantity}`).join(", ")}</p>
        </Cell>
        <Cell label="Totale"><p className="font-display text-xl text-navy">{formatEUR(o.amount_paid ?? o.amount)}</p></Cell>
        <Cell label="Pagamento">
          <StatusBadge status={o.payment_status} />
          {o.confirmation_sent_at && (
            <p className="mt-2 flex items-center gap-1.5 text-[13px] text-slate2" data-testid={`order-confirmation-sent-${ref}`}>
              <MailCheck className="h-4 w-4 shrink-0" /> Conferma inviata
            </p>
          )}
        </Cell>
      </div>
      {o.payment_status === "paid" && (
        <div className="mt-5 border-t border-dashed border-copper/25 pt-5"><FulfillmentControl order={o} /></div>
      )}
    </li>
  );
};

export const OrdersTable = ({ orders }) => {
  if (!orders.length) {
    return <p className="rounded-3xl border border-copper/20 bg-cream p-10 text-center text-lg text-slate2" data-testid="orders-empty">Nessun ordine ancora. Gli ordini appariranno qui dopo il primo checkout.</p>;
  }
  return (
    <div className="rounded-3xl border border-copper/20 bg-cream" data-testid="orders-table">
      <div className={`hidden gap-x-4 border-b border-copper/20 px-6 py-4 text-[13px] uppercase tracking-[0.15em] text-slate2 lg:grid ${COLS}`}>
        {HEAD.map((h) => <span key={h} className="font-medium">{h}</span>)}
      </div>
      <ul className="divide-y divide-copper/10">
        {orders.map((o) => <OrderRow key={o.session_id} o={o} />)}
      </ul>
    </div>
  );
};
